import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

// ---------- Types ----------
interface PageViewEvent {
  type: "pageview";
  path: string;
  title: string;
  referrer: string;
  timestamp: number;
}

interface FunnelEvent {
  type: "funnel";
  step: string;
  path: string;
  timestamp: number;
}

interface CTAEvent {
  type: "cta_click";
  label: string;
  path: string;
  timestamp: number;
}

type AnalyticsEvent = PageViewEvent | FunnelEvent | CTAEvent;

// ---------- Storage ----------
const STORAGE_KEY = "mare_analytics";
const MAX_EVENTS = 500;

function getStoredEvents(): AnalyticsEvent[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function storeEvent(event: AnalyticsEvent) {
  const events = getStoredEvents();
  events.push(event);
  // Keep only last MAX_EVENTS to avoid filling localStorage
  const trimmed = events.slice(-MAX_EVENTS);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));

  // Forward to Google Analytics (gtag) if available
  if (typeof window !== "undefined" && "gtag" in window) {
    const gtag = (window as Record<string, unknown>).gtag as (
      command: string,
      ...args: unknown[]
    ) => void;
    if (event.type === "pageview") {
      gtag("event", "page_view", {
        page_path: event.path,
        page_title: event.title,
      });
    } else if (event.type === "cta_click") {
      gtag("event", "cta_click", {
        event_label: event.label,
        page_path: event.path,
      });
    } else if (event.type === "funnel") {
      gtag("event", "funnel_step", {
        funnel_step: event.step,
        page_path: event.path,
      });
    }
  }
}

// ---------- Funnel Definitions ----------
const FUNNEL_STEPS: Record<string, string> = {
  "/": "1_home",
  "/instituto": "2_conhecer_instituto",
  "/atendimento": "3_ver_atendimento",
  "/empresas": "3_ver_empresas",
  "/contato": "4_contato",
  "/login": "5_login",
  "/meu-espaco": "6_portal_paciente",
  "/dashboard": "6_dashboard",
};

// ---------- Hook: Page Views + Funnel ----------
export function usePageTracking() {
  const location = useLocation();
  const prevPath = useRef<string>("");

  useEffect(() => {
    const path = location.pathname;
    if (path === prevPath.current) return;
    prevPath.current = path;

    // Page view
    storeEvent({
      type: "pageview",
      path,
      title: document.title,
      referrer: document.referrer,
      timestamp: Date.now(),
    });

    // Funnel step
    const step = FUNNEL_STEPS[path];
    if (step) {
      storeEvent({
        type: "funnel",
        step,
        path,
        timestamp: Date.now(),
      });
    }
  }, [location.pathname]);
}

// ---------- Hook: CTA Click Tracking ----------
export function useTrackCTA() {
  const location = useLocation();

  return useCallback(
    (label: string) => {
      storeEvent({
        type: "cta_click",
        label,
        path: location.pathname,
        timestamp: Date.now(),
      });
    },
    [location.pathname],
  );
}

// ---------- Funnel Analytics Helper ----------
export function getFunnelAnalytics() {
  const events = getStoredEvents();
  const pageviews = events.filter(
    (e): e is PageViewEvent => e.type === "pageview",
  );
  const funnelEvents = events.filter(
    (e): e is FunnelEvent => e.type === "funnel",
  );
  const ctaClicks = events.filter((e): e is CTAEvent => e.type === "cta_click");

  // Page view counts
  const pageViewCounts: Record<string, number> = {};
  for (const pv of pageviews) {
    pageViewCounts[pv.path] = (pageViewCounts[pv.path] || 0) + 1;
  }

  // Funnel progression (unique sessions based on 30min gap)
  const funnelCounts: Record<string, number> = {};
  for (const fe of funnelEvents) {
    funnelCounts[fe.step] = (funnelCounts[fe.step] || 0) + 1;
  }

  // CTA click counts
  const ctaCounts: Record<string, number> = {};
  for (const cta of ctaClicks) {
    ctaCounts[cta.label] = (ctaCounts[cta.label] || 0) + 1;
  }

  // Session flow (sequence of pages per visit)
  const sessions: string[][] = [];
  let currentSession: string[] = [];
  let lastTimestamp = 0;
  const SESSION_GAP = 30 * 60 * 1000; // 30 minutes

  for (const pv of pageviews) {
    if (pv.timestamp - lastTimestamp > SESSION_GAP && currentSession.length) {
      sessions.push(currentSession);
      currentSession = [];
    }
    currentSession.push(pv.path);
    lastTimestamp = pv.timestamp;
  }
  if (currentSession.length) sessions.push(currentSession);

  return {
    pageViewCounts,
    funnelCounts,
    ctaCounts,
    sessions,
    totalPageViews: pageviews.length,
    totalSessions: sessions.length,
  };
}
